'use client';

import { ReactElement, WheelEvent, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { PositioningStrategy, VirtualElement, createPopper, Instance as PopperInstance } from '@popperjs/core';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';

interface ContextMenuPopperWrapperProps {
  targetNode: HTMLElement | VirtualElement | null;
  children: ReactElement;
  addClassNames?: string;
  strategy?: PositioningStrategy;
}

export const ContextMenuPopperWrapper = ({
  targetNode,
  addClassNames = '',
  strategy = 'absolute',
  children,
}: ContextMenuPopperWrapperProps) => {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [scrollable, setScrollable] = useState(false);
  const topScrollArrowRef = useRef<HTMLSpanElement>(null);
  const bottomScrollArrowRef = useRef<HTMLSpanElement>(null);
  const [topScrollArrowVisible, setTopScrollArrowVisible] = useState(false);
  const [bottomScrollArrowVisible, setBottomScrollArrowVisible] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const popperInstance = useRef<PopperInstance | null>(null);

  useEffect(() => {
    if (popperElement && targetNode) {
      popperInstance.current = createPopper(targetNode, popperElement, {
        placement: 'right-start',
        strategy,
        modifiers: [],
      });
    }
  }, [popperElement, targetNode, strategy]);

  useEffect(() => {
    if (popperElement !== null) {
      setScrollable(popperElement.scrollHeight > document.body.offsetHeight);
      popperElement.scrollTop = 45;
    }
  }, [popperElement]);

  useEffect(() => {
    if (scrollable) {
      setBottomScrollArrowVisible(true);
    }
  }, [scrollable]);

  useEffect(() => {
    if (scrollableRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (popperInstance.current) {
          popperInstance.current.update();
          if (popperElement) {
            setScrollable(popperElement.scrollHeight > document.body.offsetHeight);
          }
        }
      });
      resizeObserver.observe(scrollableRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [popperElement]);

  const onScrollArrowMouseLeave = useCallback(() => {
    if (interval.current !== undefined) {
      clearInterval(interval.current);
      interval.current = undefined;
    }
  }, []);

  const scrollBottomCallback = useCallback(
    (delta = 25) => {
      if (scrollableRef.current === null) {
        return;
      }
      const absDelta = Math.abs(delta);
      const domRect = popperElement?.getBoundingClientRect();
      const scrollRect = scrollableRef.current?.getBoundingClientRect();
      if (domRect && scrollRect) {
        if (domRect.bottom < scrollRect.bottom) {
          const maxTranslate = scrollRect.bottom - domRect.bottom;
          scrollableRef.current.style.transform = `translate(0, ${
            scrollRect.top - (maxTranslate >= absDelta ? absDelta : maxTranslate)
          }px)`;
          if (!topScrollArrowVisible) {
            setTopScrollArrowVisible(true);
          }
          if (maxTranslate <= absDelta) {
            setBottomScrollArrowVisible(false);
            onScrollArrowMouseLeave();
          }
        } else {
          setBottomScrollArrowVisible(false);
          onScrollArrowMouseLeave();
        }
      }
    },
    [popperElement, onScrollArrowMouseLeave, topScrollArrowVisible]
  );

  const scrollTopCallback = useCallback(
    (delta = 25) => {
      if (scrollableRef.current === null) {
        return;
      }
      const absDelta = Math.abs(delta);
      const domRect = popperElement?.getBoundingClientRect();
      const scrollRect = scrollableRef.current?.getBoundingClientRect();
      if (domRect && scrollRect) {
        if (domRect.top > scrollRect.top) {
          const maxTranslate = domRect.top - scrollRect.top;
          scrollableRef.current.style.transform = `translate(0, ${
            scrollRect.top + (maxTranslate >= absDelta ? absDelta : maxTranslate)
          }px)`;
          if (!bottomScrollArrowVisible) {
            setBottomScrollArrowVisible(true);
          }
          if (maxTranslate <= absDelta) {
            setTopScrollArrowVisible(false);
            onScrollArrowMouseLeave();
          }
        } else {
          setTopScrollArrowVisible(false);
          onScrollArrowMouseLeave();
        }
      }
    },
    [popperElement, onScrollArrowMouseLeave, bottomScrollArrowVisible]
  );

  const onTopMouseEnter = useCallback(() => {
    interval.current = setInterval(() => {
      scrollTopCallback();
    }, 100);
  }, [scrollTopCallback]);

  const onBottomMouseEnter = useCallback(() => {
    interval.current = setInterval(() => {
      scrollBottomCallback();
    }, 100);
  }, [scrollBottomCallback]);

  const onMouseWheel = useCallback(
    (event: WheelEvent) => {
      if (event.deltaY > 0) {
        scrollBottomCallback(event.deltaY);
      } else {
        scrollTopCallback(event.deltaY);
      }
    },
    [scrollBottomCallback, scrollTopCallback]
  );

  return (
    <div ref={setPopperElement} className={addClassNames}>
      {scrollable && (
        <>
          <span
            ref={topScrollArrowRef}
            className={classNames(
              'top-scroll-arrow absolute top-0 left-0 w-[calc(100%-4px)] ml-1 z-10 flex justify-center bg-white/75 py-2',
              {
                hidden: !topScrollArrowVisible,
              }
            )}
            onMouseEnter={onTopMouseEnter}
            onMouseLeave={onScrollArrowMouseLeave}
          >
            <Icon icon={IconsMap.ARROW_2_UP} className="w-4" />
          </span>
          <span
            ref={bottomScrollArrowRef}
            className={classNames(
              'bottom-scroll-arrow absolute bottom-0 left-0 ml-1 w-[calc(100%-4px)] z-10 flex justify-center bg-white/75 py-2',
              {
                hidden: !bottomScrollArrowVisible,
              }
            )}
            onMouseEnter={onBottomMouseEnter}
            onMouseLeave={onScrollArrowMouseLeave}
          >
            <Icon icon={IconsMap.ARROW_2_DOWN} className="w-4" />
          </span>
        </>
      )}
      <div
        ref={scrollableRef}
        className="relative transition-transform"
        style={{ transform: 'translate(0, 0)' }}
        onWheel={onMouseWheel}
      >
        {children}
      </div>
    </div>
  );
};
